import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";
import usePageShell from "../hooks/usePageShell.js";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import useAnalyticsContext from "../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../lib/analytics";
import PhotoSharingEditorial from "../components/wedding-photo-sharing/PhotoSharingEditorial";
import InvitationHero from "../components/digital-wedding-invitation/InvitationHero";
import InvitationSteps from "../components/digital-wedding-invitation/InvitationSteps";
import InvitationDemo from "../components/digital-wedding-invitation/InvitationDemo";
import InvitationArc from "../components/digital-wedding-invitation/InvitationArc";
import InvitationPlan from "../components/digital-wedding-invitation/InvitationPlan";
import InvitationFaq from "../components/digital-wedding-invitation/InvitationFaq";
import InvitationFinalCta from "../components/digital-wedding-invitation/InvitationFinalCta";
import { DWI_SECTION } from "../components/digital-wedding-invitation/sections.js";

/**
 * /digital-wedding-invitation
 *
 * The second SEO/GEO page, and the counterpart to /wedding-photo-sharing: that
 * one is the "During and After" of the arc, this one is the "Before". Same
 * visual language on purpose — it should read as the next chapter, not a
 * different site.
 *
 * ── Crawlability ──────────────────────────────────────────────────────────
 * This route also ships as a real static document (`digital-wedding-invitation
 * .html` at the project root, built as a third Vite entry). That file — not
 * this component — is what a crawler that does not run JavaScript receives, and
 * it carries the title, description, canonical, robots directive, social tags,
 * JSON-LD and every heading, paragraph and FAQ answer below. React replaces it
 * on mount. `digitalWeddingInvitation.seo.test.mjs` asserts the two never
 * drift apart, and pins the claims the product audit ruled out.
 *
 * The meta hook still runs, because a visitor who arrives by client-side
 * navigation gets no static head.
 *
 * ── The one claim boundary that governs the whole page ────────────────────
 * Everything described here is Heirloom-only, and not partially: the public
 * event endpoint strips the entire invitation payload for any plan other than
 * `pro`, so a Blossom or Bouquet couple's invitation URL renders "Coming soon".
 * `InvitationPlan` is not decoration — it is the section that keeps the rest of
 * the page honest, which is why it sits before the FAQ rather than after it.
 *
 * ── Page shell ────────────────────────────────────────────────────────────
 * `usePageShell` — the shared bootstrap every page runs. See the hook for why
 * it re-initialises the Webflow engine from a layout effect rather than after
 * paint.
 */
/**
 * This page's language-neutral path. The canonical and the alternates are
 * derived from it through the page registry rather than written out: the
 * canonical is self-referencing (a Greek page canonicalised to English is a
 * Greek page asking not to be indexed), and the alternates are the reciprocal
 * set the static documents declare, from the same one source.
 */
const PATH = "/digital-wedding-invitation";
const STEPS_SECTION_ID = "how-it-works";

const DigitalWeddingInvitation = () => {
  const analyticsContext = useAnalyticsContext();

  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.digitalWeddingInvitation.title",
    descriptionKey: "meta.digitalWeddingInvitation.description",
    canonical: canonicalUrlFor(PATH, language),
    alternates: alternatesFor(PATH),
    robots: "index, follow",
  });

  // The RSVP differentiator is the argument the page is really making, so it
  // gets its own impression. `PhotoSharingEditorial` stays presentational —
  // the observer rides on a wrapper here rather than being pushed into a
  // component two pages share.
  const rsvpRef = useSectionOnceObserver(() =>
    trackSectionViewed(DWI_SECTION.RSVP, analyticsContext),
  );

  usePageShell();

  return (
    <div className="page-wrapper wps-page dwi-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <InvitationHero />

        <PhotoSharingEditorial
          headingKey="digitalWeddingInvitation.whatItIs.heading"
          bodyKeys={[
            "digitalWeddingInvitation.whatItIs.body1",
            "digitalWeddingInvitation.whatItIs.body2",
          ]}
          noteKey="digitalWeddingInvitation.whatItIs.note"
          image={{
            src: "/assets/images/lovely_couple_004.jpeg",
            altKey: "digitalWeddingInvitation.whatItIs.imageAlt",
            width: 1280,
            height: 714,
          }}
        />

        <InvitationSteps id={STEPS_SECTION_ID} />

        <div ref={rsvpRef}>
          <PhotoSharingEditorial
            headingKey="digitalWeddingInvitation.rsvp.heading"
            bodyKeys={[
              "digitalWeddingInvitation.rsvp.body1",
              "digitalWeddingInvitation.rsvp.body2",
            ]}
            noteKey="digitalWeddingInvitation.rsvp.note"
            image={{
              src: "/assets/images/ceremony.jpeg",
              altKey: "digitalWeddingInvitation.rsvp.imageAlt",
              width: 1280,
              height: 714,
            }}
            imageSide="left"
          />
        </div>

        <PhotoSharingEditorial
          headingKey="digitalWeddingInvitation.personal.heading"
          bodyKeys={[
            "digitalWeddingInvitation.personal.body1",
            "digitalWeddingInvitation.personal.body2",
          ]}
          noteKey="digitalWeddingInvitation.personal.note"
        />

        <PhotoSharingEditorial
          headingKey="digitalWeddingInvitation.guestList.heading"
          bodyKeys={[
            "digitalWeddingInvitation.guestList.body1",
            "digitalWeddingInvitation.guestList.body2",
            "digitalWeddingInvitation.guestList.body3",
          ]}
          noteKey="digitalWeddingInvitation.guestList.note"
          image={{
            src: "/assets/images/invitation/invitation-3.jpg",
            altKey: "digitalWeddingInvitation.guestList.imageAlt",
            width: 6558,
            height: 3659,
          }}
        />

        <InvitationDemo />

        <InvitationArc />

        <InvitationPlan />

        <InvitationFaq />

        <InvitationFinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default DigitalWeddingInvitation;
